/* eslint-disable complexity */
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Route, Router, UrlTree } from '@angular/router';
import { AuthState, ProfileLocation, RoutingFunction, TMHCorporateAdminAuthState } from '../components/auth/definitions';
import { CommonErrorService } from '../components/common-error/common-error.service';
import { AppConstants, expAppConstants } from '../constants/portal-constants';
import { ApiResponse } from '../definitions/api-response.interface';
import { ApiService } from '../services/api/api.service';
import { UserRegistrationStatus } from '../services/api/definitions';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { LoggerService } from '../services/logger/logger.service';
import { SharedService } from '../services/shared/shared.service';
import { SharedUtilService } from '../services/shared/shared-util.service';
import { AuthFactory } from '../components/auth';
import { ApplicationPath, ErrorMessage } from '../services/shared/shared-enums';
import { SharedApiService } from '../services/shared/shared-api-call.service';

@Injectable({ providedIn: 'root' })
export class AccessGuard implements CanActivate 
{
	user:AuthState= AuthFactory.buildState();
	constructor(private router: Router,
        private errorService: CommonErrorService,
        private apiService: ApiService,
        private loggerService: LoggerService,
		private localStorageService: LocalStorageService,
		private sharedService: SharedService,
		private sharedUtilService: SharedUtilService,
		private sharedApiService: SharedApiService,
        @Inject(expAppConstants) readonly appConstantsRef: AppConstants)
	{ 
	}

	async canActivate(route:ActivatedRouteSnapshot): Promise<boolean | UrlTree>
	{
		try 
		{			
			const status: ApiResponse = await this.getUserRegistrationStatus();
			const responseObject = <UserRegistrationStatus>status.data;
			responseObject.registrationStatus = responseObject.registrationStatus === 'pending_approval' && responseObject.orgAdminPending? 'pending_business_approval' : responseObject.registrationStatus;
			const routingFunction: RoutingFunction | undefined = this.sharedUtilService.getRoutingFunction(responseObject);
			const isTmhCustomerSupportRole = this.localStorageService.getCustomerToDashboard();
			return new Promise((resolve) => 
			{
				if (routingFunction !== undefined) 
				{
					if(this.sharedUtilService.isCustomerSupportUser(responseObject, isTmhCustomerSupportRole))
					{
						this.sharedUtilService.isTmhCustomerSupportRole = true;
						this.localStorageService.setItems('isTmhCustomerSupportRole', 'true');
						return resolve(this.handleTmhCustomerSupportRouting(routingFunction, route));
					}
					if(this.sharedUtilService.isTmhCorporateAdminUser(responseObject, isTmhCustomerSupportRole))
					{
						this.sharedUtilService.isTmhCorporateAdmin = true;
						this.localStorageService.setItems('isTmhCorporateAdmin', 'true');
						return resolve(this.handleTmhCorporateAdminRouting(routingFunction, route));
					}
					else if (responseObject.hasOwnProperty('registrationStatus') && responseObject.registrationStatus === 'approved' || isTmhCustomerSupportRole) 
					{
						this.localStorageService.setItems('isPortalUser', 'true');
						this.handleTmhNonCorporateAdminRouting(route, isTmhCustomerSupportRole);
						resolve(true);
					}
					else 
					{
						this.handlePendingAndNotApproved(responseObject, routingFunction);
						resolve(false);
					}

				}
			});

		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any	
		catch (error: any) 
		{
			return new Promise((resolve) => 
			{
				this.redirectOnError(error);
				resolve(false);
			});
				
		}
			
	}

	private handlePendingAndNotApproved(response : UserRegistrationStatus, routingFunction: RoutingFunction | undefined)
	{
		this.sharedUtilService.handlePendingAndNotApprovedState(response);
		this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
	}

	private redirectOnError(error: HttpErrorResponse) 
	{
		const errorMessage =
            error?.error?.statusCode === 401
            	? error.error.message
            	: this.appConstantsRef.messages.filter(
            		(x) => x.category === 'Error' && x.tags.includes('default')
            	)[0]?.value;
		this.sharedUtilService.triggerErrorService(errorMessage, error?.error?.statusCode, error?.error?.id);
		this.sharedService.navigateMethod(this.router, ApplicationPath.unAuthError);
	}

	private async getUserRegistrationStatus(): Promise<ApiResponse> 
	{
		try 
		{
			return await this.apiService.getUserRegistrationStatus();
		}
		catch (error) 
		{
			this.loggerService.log('Error redirecting to logout', error);
			throw error;
		}
	}

	getLoggedInUserDetails() 
	{
		this.apiService.getLoggedInUserObs().subscribe({
			next: (res: ApiResponse) =>
			{

				const user = <AuthState>(res.data);
				if (user)
				{
					this.user = user;
					this.localStorageService.setLoggedInUser(this.localStorageService.striginifyJSON(user));
				}
			},
			error: () =>
			{
				this.sharedUtilService.triggerErrorService(ErrorMessage.commonError, 100, '401');
				this.sharedService.navigateMethod(this.router, ApplicationPath.unAuthError);
			}
		});
		this.apiService.getLoggedInUserLocations().subscribe({
			next: (res: ApiResponse) =>
			{
				const locations:ProfileLocation = <ProfileLocation>(res.data);
				if (locations)
				{
					this.localStorageService.setLoggedInUserLocation(this.localStorageService.striginifyJSON(locations));
				}
			},
			error: () =>
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		});
	}
	isRouteMatchingPath(route: Route | null, applicationPath: string)
	{
		return !!(route?.path?.includes(applicationPath.slice(1)));
	}
	handleTmhCorporateAdminRouting(routingFunction: RoutingFunction, route: ActivatedRouteSnapshot)
	{
		this.getCorporateAdminDetails(routingFunction);
		if(this.isRouteMatchingPath(route.routeConfig, ApplicationPath.customerportal))
		{
			return true;
		}
		else if(!this.isRouteMatchingPath(route.routeConfig, ApplicationPath.unAuthError))
		{
			this.handleCommonError(ErrorMessage.unauthorizedError, 401, '');
			return false;
		}
		return false;
	}
	handleTmhCustomerSupportRouting(routingFunction: RoutingFunction, route: ActivatedRouteSnapshot)
	{
		this.getCustomerSupportDetails(routingFunction);
		if(this.isRouteMatchingPath(route.routeConfig, ApplicationPath.customerSupport))
		{
			return true;
		}
		else if(!this.isRouteMatchingPath(route.routeConfig, ApplicationPath.unAuthError))
		{
			this.handleCommonError(ErrorMessage.unauthorizedError, 401, '');
			return false;
		}
		return false;
	}
	private getCorporateAdminDetails(routingFunction: RoutingFunction | undefined)
	{
		if(!this.localStorageService.getTmhCorporateAdminDetails())
			this.getLoggedInTmhCorporateAdminDetails(routingFunction);
	}
	getCustomerSupportDetails(routingFunction: RoutingFunction | undefined)
	{
		if(!this.localStorageService.getTmhCustomerSupportDetails())
			this.getLoggedInTmhCustomerSupportDetails(routingFunction);
	}
	handleTmhNonCorporateAdminRouting(route: ActivatedRouteSnapshot, isTmhCustomerSupportRole: boolean = false)
	{
		if(!this.localStorageService.getLoggedInUser())
			this.getLoggedInUserDetails();
		if(this.isRouteMatchingPath(route.routeConfig, ApplicationPath.customerportal) && !isTmhCustomerSupportRole)
		{	
			this.handleCommonError(ErrorMessage.unauthorizedError, 401, '');
		}
	}
	getLoggedInTmhCorporateAdminDetails(routingFunction : RoutingFunction | undefined)
	{
		this.apiService.getLoggedInTmhCorporateAdminDetails().subscribe({ next: (res)=>
		{
			if(res)
			{
				const user = <TMHCorporateAdminAuthState>(res.data);
				this.sharedApiService.mapTmhCorporateAdminUserSession(user);
				if(this.localStorageService.getTmhCorporateAdminDetails())
				{
					this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
				}
			}
			else
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		}
		});
	}
	getLoggedInTmhCustomerSupportDetails(routingFunction : RoutingFunction | undefined)
	{
		this.apiService.getLoggedInTmhCustomerSupportDetails().subscribe({ next: (res)=>
		{
			if(res)
			{
				const user = <TMHCorporateAdminAuthState>(res.data);
				this.sharedApiService.mapTmhCustomerSupportUserSession(user);
				if(this.localStorageService.getTmhCustomerSupportDetails())
				{
					this.sharedUtilService.handleRoutingFunction(routingFunction, this.router);
				}
			}
			else
			{
				this.handleCommonError(ErrorMessage.commonError, 100, '401');
			}
		}
		});
	}
	handleCommonError(status: string, statusCode: number, id:string)
	{
		this.sharedUtilService.triggerErrorService(status, statusCode, id);
		this.sharedService.navigateMethod(this.router, ApplicationPath.unAuthError);
	}
}
